import { useState, useEffect, useCallback } from "react";
import { TabState } from "../application/service/smart-apply/procedure-detail/tab-state";
import { SurveyTabState } from "@graffer-inc/smart-apply";
import Analytics from "../common/analytics";

type Product = "SMART_APPLY" | "SURVEY";

const ALERT_MESSAGES: Record<Product, string> = {
  SMART_APPLY:
    "申請フォームの変更内容が保存されていません。変更内容を破棄して移動しますか？",
  SURVEY:
    "アンケートフォームの変更内容が保存されていません。変更内容を破棄して移動しますか？",
};

type FormUnsavedHookProps = {
  tabState: TabState | SurveyTabState;
  switchTab: ((tab: TabState) => void) | ((tab: SurveyTabState) => void);
  object?: { name: string; id: string };
  product: Product;
};

type ConfirmFunc<T = object | undefined> = T extends undefined
  ? () => void
  : (arg: T) => void;

export const useFormUnsaved = ({
  tabState,
  switchTab,
  object,
  product,
}: FormUnsavedHookProps) => {
  const [isFormUnsaved, setIsFormUnsaved] = useState(false);
  useEffect(() => {
    if (!tabState.equals("FORM")) {
      setIsFormUnsaved(false);
    }
  }, [tabState]);
  useEffect(() => {
    // See https://developer.mozilla.org/ja/docs/Web/API/WindowEventHandlers/onbeforeunload
    const handleBeforeUnloadEvent = (e: BeforeUnloadEvent) => {
      if (isFormUnsaved) {
        // イベントをキャンセルする
        e.preventDefault();
        // Chrome では returnValue を設定する必要がある
        e.returnValue = "";
        notifyToGA(product, object);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnloadEvent);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnloadEvent);
  }, [isFormUnsaved, product, object]);

  const confirmWhenFormUnsaved = useCallback(
    <T extends object | undefined = undefined>(
      onConfirm: ConfirmFunc<T>,
      args?: T,
    ) => {
      const action = () => {
        if (args !== undefined) {
          onConfirm(args);
        } else {
          (onConfirm as ConfirmFunc<undefined>)();
        }
      };

      if (!isFormUnsaved) {
        action();
        setIsFormUnsaved(false);
        return;
      }
      if (isFormUnsaved) {
        if (window.confirm(ALERT_MESSAGES[product])) {
          action();
          setIsFormUnsaved(false);
        }

        notifyToGA(product, object);
      }
    },
    [isFormUnsaved, product, object],
  );

  const handleSwitchTab = useCallback(
    (tab: TabState | SurveyTabState) => {
      confirmWhenFormUnsaved<TabState | SurveyTabState>(switchTab, tab);
    },
    [switchTab, confirmWhenFormUnsaved],
  );

  return { setIsFormUnsaved, handleSwitchTab, confirmWhenFormUnsaved };
};

const notifyToGA = (
  product: Product,
  object?: { id: string; name: string },
) => {
  product === "SURVEY"
    ? Analytics.alertWhenSurveyFormUnsaved(object)
    : Analytics.alertWhenFormUnsaved(object);
};
